import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { lotesApi, eventosApi } from '../../lib/api';
import { Plus, Pencil, Trash2, Calendar, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const LotesPage = () => {
  const [lotes, setLotes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLote, setSelectedLote] = useState(null);
  const [filterEvento, setFilterEvento] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    evento_id: '',
    nome_lote: '',
    preco: '',
    quantidade_total: '',
    data_inicio: '',
    data_fim: '',
    status: 'ativo',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [lotesRes, eventosRes] = await Promise.all([
        lotesApi.list(),
        eventosApi.list(),
      ]);
      setLotes(lotesRes.data);
      setEventos(eventosRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      evento_id: '',
      nome_lote: '',
      preco: '',
      quantidade_total: '',
      data_inicio: '',
      data_fim: '',
      status: 'ativo',
    });
    setSelectedLote(null);
  };

  const openCreateDialog = () => {
    resetForm();
    if (eventos.length > 0) {
      setFormData(prev => ({ ...prev, evento_id: eventos[0].id }));
    }
    setDialogOpen(true);
  };

  const openEditDialog = (lote) => {
    setSelectedLote(lote);
    setFormData({
      evento_id: lote.evento_id,
      nome_lote: lote.nome_lote,
      preco: lote.preco.toString(),
      quantidade_total: lote.quantidade_total.toString(),
      data_inicio: lote.data_inicio ? lote.data_inicio.slice(0, 16) : '',
      data_fim: lote.data_fim ? lote.data_fim.slice(0, 16) : '',
      status: lote.status,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (lote) => {
    setSelectedLote(lote);
    setDeleteDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      preco: parseFloat(formData.preco),
      quantidade_total: parseInt(formData.quantidade_total),
      data_inicio: new Date(formData.data_inicio).toISOString(),
      data_fim: new Date(formData.data_fim).toISOString(),
    };

    try {
      if (selectedLote) {
        // Don't send evento_id on update
        const { evento_id, ...updatePayload } = payload;
        await lotesApi.update(selectedLote.id, updatePayload);
        toast.success('Lote atualizado com sucesso!');
      } else {
        await lotesApi.create(payload);
        toast.success('Lote criado com sucesso!');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      logger.error('Error saving lote:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar lote');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLote) return;

    try {
      await lotesApi.delete(selectedLote.id);
      toast.success('Lote deletado com sucesso!');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      logger.error('Error deleting lote:', error);
      toast.error('Erro ao deletar lote');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMM, HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getEventoName = (eventoId) => {
    const evento = eventos.find(e => e.id === eventoId || e.evento_id === eventoId);
    return evento?.nome_evento || evento?.nome || 'Evento não encontrado';
  };

  const filteredLotes = filterEvento === 'all' 
    ? lotes 
    : lotes.filter(l => l.evento_id === filterEvento);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="lotes-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Lotes</h1>
            <p className="text-gray-400">Gerencie os lotes de ingressos</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={filterEvento} onValueChange={setFilterEvento}>
              <SelectTrigger className="w-[200px] bg-obsidian-50 border-white/10 text-white">
                <SelectValue placeholder="Filtrar por evento" />
              </SelectTrigger>
              <SelectContent className="bg-obsidian-200 border-white/10">
                <SelectItem value="all" className="text-white hover:bg-white/10">Todos os eventos</SelectItem>
                {eventos.map(evento => (
                  <SelectItem key={evento.id} value={evento.id} className="text-white hover:bg-white/10">
                    {evento.nome_evento || evento.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={openCreateDialog}
              className="bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white"
              disabled={eventos.length === 0}
              data-testid="create-lote-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Novo Lote
            </Button>
          </div>
        </div>

        {eventos.length === 0 ? (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="py-16 text-center">
              <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Crie um evento primeiro para adicionar lotes.</p>
            </CardContent>
          </Card>
        ) : filteredLotes.length === 0 ? (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="py-16 text-center">
              <Layers className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">Nenhum lote encontrado.</p>
              <Button onClick={openCreateDialog} className="bg-kairos-orange text-white">
                Criar Primeiro Lote
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <Card className="bg-obsidian-200 border-white/10">
              <CardContent className="p-0">
                <table className="table-dark">
                  <thead>
                    <tr>
                      <th>Lote</th>
                      <th>Evento</th>
                      <th>Preço</th>
                      <th>Vendidos</th>
                      <th>Período</th>
                      <th>Status</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLotes.map((lote) => (
                      <tr key={lote.id} data-testid={`lote-row-${lote.id}`}>
                        <td className="text-white font-medium">{lote.nome_lote}</td>
                        <td className="text-gray-400">{getEventoName(lote.evento_id)}</td>
                        <td className="text-kairos-orange font-medium">{formatCurrency(lote.preco)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className="text-white">{lote.quantidade_vendida}</span>
                            <span className="text-gray-500">/ {lote.quantidade_total}</span>
                          </div>
                          <div className="w-24 h-1.5 bg-obsidian rounded-full mt-1">
                            <div 
                              className="h-full bg-kairos-orange rounded-full"
                              style={{ width: `${(lote.quantidade_vendida / lote.quantidade_total) * 100}%` }}
                            ></div>
                          </div>
                        </td>
                        <td className="text-gray-400 text-sm">
                          {formatDate(lote.data_inicio)} - {formatDate(lote.data_fim)}
                        </td>
                        <td>
                          {lote.status === 'ativo' 
                            ? <span className="badge-success">Ativo</span>
                            : <span className="badge-error">Encerrado</span>
                          }
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(lote)}
                              className="text-gray-400 hover:text-white"
                              data-testid={`edit-lote-${lote.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(lote)}
                              className="text-gray-400 hover:text-red-500"
                              data-testid={`delete-lote-${lote.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {selectedLote ? 'Editar Lote' : 'Novo Lote'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!selectedLote && (
                <div>
                  <Label className="text-gray-300">Evento</Label>
                  <Select 
                    value={formData.evento_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, evento_id: value }))}
                  >
                    <SelectTrigger className="mt-1 bg-obsidian-50 border-white/10 text-white" data-testid="lote-evento-select">
                      <SelectValue placeholder="Selecione o evento" />
                    </SelectTrigger>
                    <SelectContent className="bg-obsidian-200 border-white/10">
                      {eventos.map(evento => (
                        <SelectItem key={evento.id} value={evento.id} className="text-white hover:bg-white/10">
                          {evento.nome_evento || evento.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-gray-300">Nome do Lote</Label>
                <Input
                  name="nome_lote"
                  value={formData.nome_lote}
                  onChange={handleInputChange}
                  required
                  className="mt-1 bg-obsidian-50 border-white/10 text-white"
                  placeholder="Ex: 1º Lote - Promocional"
                  data-testid="lote-nome-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Preço (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    name="preco"
                    value={formData.preco}
                    onChange={handleInputChange}
                    required
                    min="0"
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    placeholder="150.00"
                    data-testid="lote-preco-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Quantidade</Label>
                  <Input
                    type="number"
                    name="quantidade_total"
                    value={formData.quantidade_total}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    placeholder="50"
                    data-testid="lote-quantidade-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Início das Vendas</Label>
                  <Input
                    type="datetime-local"
                    name="data_inicio"
                    value={formData.data_inicio}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    data-testid="lote-data-inicio-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Fim das Vendas</Label>
                  <Input
                    type="datetime-local"
                    name="data_fim"
                    value={formData.data_fim}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    data-testid="lote-data-fim-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger className="mt-1 bg-obsidian-50 border-white/10 text-white" data-testid="lote-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-obsidian-200 border-white/10">
                    <SelectItem value="ativo" className="text-white hover:bg-white/10">Ativo</SelectItem>
                    <SelectItem value="encerrado" className="text-white hover:bg-white/10">Encerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-kairos-orange text-white hover:bg-kairos-orange-light"
                  data-testid="save-lote-btn"
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-obsidian-200 border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Deletar Lote?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Esta ação não pode ser desfeita. O lote "{selectedLote?.nome_lote}" será permanentemente removido.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/20 text-white hover:bg-white/10">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-lote"
              >
                Deletar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default LotesPage;
